/**
 * Nookie Wishlist — localStorage-based wishlist with drawer support
 */
(function () {
  const STORAGE_KEY = 'nookie_wishlist';

  /* ── Storage helpers ─────────────────────────────────────────────────── */

  function getWishlist() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  }

  function saveWishlist(list) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  }

  function isInWishlist(productId) {
    return getWishlist().some((item) => item.id === productId);
  }

  function addToWishlist(productId, productHandle, productTitle, productImage, productPrice, productUrl) {
    const list = getWishlist();
    if (!list.some((i) => i.id === productId)) {
      list.push({ id: productId, handle: productHandle, title: productTitle, image: productImage, price: productPrice, url: productUrl });
      saveWishlist(list);
    }
  }

  function removeFromWishlist(productId) {
    saveWishlist(getWishlist().filter((i) => i.id !== productId));
  }

  function toggleWishlist(productId, productHandle, productTitle, productImage, productPrice, productUrl) {
    if (isInWishlist(productId)) {
      removeFromWishlist(productId);
      return false;
    } else {
      addToWishlist(productId, productHandle, productTitle, productImage, productPrice, productUrl);
      return true;
    }
  }

  /* ── Count badge ─────────────────────────────────────────────────────── */

  function updateCount() {
    const count = getWishlist().length;
    document.querySelectorAll('.wishlist-count').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  /* ── Sync all heart buttons on page ─────────────────────────────────── */

  function syncButtons() {
    document.querySelectorAll('[data-wishlist-btn]').forEach((btn) => {
      const id = btn.dataset.productId;
      const active = isInWishlist(id);
      btn.classList.toggle('wishlist-btn--active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    // Also sync the title-row wishlist button
    document.querySelectorAll('.product-title-wishlist-btn').forEach((btn) => {
      const id = btn.dataset.productId;
      const active = isInWishlist(id);
      btn.classList.toggle('wishlist-btn--active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  /* ── Render wishlist drawer ──────────────────────────────────────────── */

  function formatMoney(cents) {
    return (cents / 100).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
  }

  function renderDrawer() {
    const list = getWishlist();
    const emptyEl = document.getElementById('wishlist-empty');
    const itemsEl = document.getElementById('wishlist-items');
    const moreEl = document.getElementById('wishlist-more-to-love');
    if (!itemsEl) return;

    itemsEl.innerHTML = '';

    if (emptyEl) emptyEl.hidden = list.length > 0;
    if (moreEl) {
      const wishIds = new Set(list.map((item) => String(item.id)));
      moreEl.querySelectorAll('[data-product-id]').forEach((el) => {
        el.hidden = wishIds.has(String(el.dataset.productId));
      });
      const hasVisible = [...moreEl.querySelectorAll('[data-product-id]')].some((el) => !el.hidden);
      moreEl.hidden = !hasVisible;
    }

    if (list.length === 0) {
      return;
    }

    list.forEach((item) => {
      const li = document.createElement('li');
      li.className = 'wishlist-item';
      li.innerHTML = `
        <a href="${item.url}" class="wishlist-item__image-link">
          <img src="${item.image}" alt="${item.title}" class="wishlist-item__image" loading="lazy" width="80" height="100">
        </a>
        <div class="wishlist-item__info">
          <a href="${item.url}" class="wishlist-item__title">${item.title}</a>
          <p class="wishlist-item__price text-subdued">${formatMoney(item.price)}</p>
          <a href="${item.url}" class="wishlist-item__cta button button--sm">VIEW PRODUCT</a>
        </div>
        <button class="wishlist-item__remove tap-area" data-remove-id="${item.id}" aria-label="Remove from wishlist">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 14 14"><path stroke="currentColor" stroke-linecap="round" d="M2 2l10 10M12 2 2 12"/></svg>
        </button>
      `;

      li.querySelector('[data-remove-id]').addEventListener('click', (e) => {
        removeFromWishlist(e.currentTarget.dataset.removeId);
        updateCount();
        syncButtons();
        renderDrawer();
      });

      itemsEl.appendChild(li);
    });
  }

  /* ── Open drawer ─────────────────────────────────────────────────────── */

  function openWishlistDrawer() {
    renderDrawer();
    const drawer = document.getElementById('wishlist-drawer');
    if (drawer) drawer.show();
  }

  /* ── Init ────────────────────────────────────────────────────────────── */

  function init() {
    updateCount();
    syncButtons();

    /* Heart buttons on product cards */
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-wishlist-btn]');
      if (!btn) return;
      e.preventDefault();

      const { productId, productHandle, productTitle, productImage, productPrice, productUrl } = btn.dataset;
      const added = toggleWishlist(productId, productHandle, productTitle, productImage, parseInt(productPrice, 10), productUrl);

      btn.classList.toggle('wishlist-btn--active', added);
      btn.setAttribute('aria-pressed', added ? 'true' : 'false');
      updateCount();
    });

    /* Header heart → open drawer */
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-wishlist-drawer-trigger]');
      if (!trigger) return;
      e.preventDefault();
      openWishlistDrawer();
    });

    /* Re-sync after quick-buy modal / page navigation */
    document.addEventListener('shopify:section:load', () => {
      updateCount();
      syncButtons();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Expose for external use */
  window.NookieWishlist = { add: addToWishlist, remove: removeFromWishlist, toggle: toggleWishlist, isIn: isInWishlist, get: getWishlist, open: openWishlistDrawer };
})();
